// pages/master/tabs/SummaryTab.jsx
import React, { useMemo } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { Home, MapPin, Droplet, CreditCard, Hash, Calendar } from "react-feather";

const n0 = (v) => Number(v || 0);
const money = (v) => `₹${Math.round(n0(v)).toLocaleString("en-IN")}`;
const safe = (v, dash = "-") => (v === null || v === undefined || v === "" ? dash : v);
const yn = (v) => (String(v ?? "").toLowerCase() === "yes" ? "Yes" : "No");

const fmtDate = (v) => {
  if (!v || v === "-" || v === "N/A" || v === null) return "N/A";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString("en-GB");
  } catch {
    return String(v);
  }
};

const num = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString("en-IN") : safe(v);
};

const InfoRow = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      padding: "8px 0",
      borderBottom: "1px solid #f0f2f5",
      gap: 12,
    }}
  >
    <div style={{ width: 170, fontSize: 13, color: "#6b7280" }}>{label}</div>
    <div style={{ fontSize: 13, color: "#111827", fontWeight: 500, flex: 1, textAlign: "right" }}>
      {value}
    </div>
  </div>
);

const SectionCard = ({ title, icon, items }) => {
  return (
    <Card style={{ border: "1px solid #e9ecef", borderRadius: 12 }} className="h-100">
      <Card.Body style={{ padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#e6f2ff",
              color: "#0066cc",
            }}
          >
            {icon}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{title}</div>
        </div>

        <div>
          {items.map(([k, v], i) => (
            <InfoRow key={i} label={k} value={v} />
          ))}
        </div>
      </Card.Body>
    </Card>
  );
};

export default function SummaryTab({ house, summary, metrics }) {
  const h = house || {};
  const s = summary || {};
  const m = metrics || {};

  const mReceipts = m?.receipts || {};
  const mBills = m?.bills || {};
  const mDCB = m?.dcb || {};

  const cards = useMemo(() => {
    const netOutstanding = n0(s.total_bill) - n0(s.total_paid);
    const netLabel = netOutstanding < 0 ? "Credit Balance" : "Total Due";
    const netValue = netOutstanding < 0 ? money(Math.abs(netOutstanding)) : money(netOutstanding);

    return [
      {
        title: "Basic Details",
        icon: <Home size={18} />,
        items: [
          ["Home ID", safe(h.home_id)],
          ["Door No", safe(h.door_no)],
          ["QR Code", safe(h.qr_code)],
          ["Family Head", safe(h.family_head)],
          ["Mobile", safe(h.mobile)],
          ["Status", safe(h.status)],
        ],
      },
      {
        title: "Location Details",
        icon: <MapPin size={18} />,
        items: [
          ["Ward", `${safe(h.ward_name)} (${safe(h.ward_id)})`],
          ["Panchayath", safe(h.pan_name)],
          ["Taluk", safe(h.taluk_name)],
          ["District", safe(h.dist_name)],
          ["PAN ID", safe(h.pan_id)],
        ],
      },
      {
        title: "Connection Details",
        icon: <Droplet size={18} />,
        items: [
          ["Water Rate", safe(h.meter_type_name || h.mtr_type)],
          ["Water Connection", yn(h.is_water)],
          ["SLRM Collection", yn(h.is_slrm)],
          ["Bill Together", yn(h.slrm_water_together)],
          ["Slrm Rate", safe(h.slrm_type_name)],
        ],
      },
      {
        title: "Current Balances",
        icon: <CreditCard size={18} />,
        items: [
          ["Total Due (Overall)", money(h.balance)],
          ["Water Due", money(h.water_balance)],
          ["SLRM Due", money(h.slrm_bal)],
          ["Opening Balance", money(h.op_balance)],
          ["Opening Water", money(h.op_water_balance)],
          ["Opening SLRM", money(h.op_slrm_bal)],
        ],
      },
      {
        title: "Last Activity",
        icon: <Hash size={18} />,
        items: [
          ["Last Reading", safe(h.last_reading)],
          ["Last Water Bill Date", fmtDate(h.last_bill_date)],
          ["Last SLRM Bill Date", fmtDate(h.slrm_last_bill_date)],
          ["Last Payment Date", fmtDate(h.last_payment_date)],
          ["Added On", fmtDate(h.added || h.added_on || h.created_at)],
        ],
      },

      {
        title: "Bill & Payment Summary",
        icon: <CreditCard size={18} />,
        items: [
          ["Total Bill Amount", money(s.total_bill)],
          ["Total Paid Amount", money(s.total_paid)],
          [netLabel, netValue],
          ["Water Bill Amount", money(s.water_bill)],
          ["Water Paid Amount", money(s.water_paid)],
          ["SLRM Bill Amount", money(s.slrm_fee)],
          ["SLRM Paid Amount", money(s.slrm_paid)],
        ],
      },

      {
        title: "Overall Payment Status",
        icon: <Hash size={18} />,
        items: [
          ["Total Bill Amount", money(mDCB?.demand)],
          ["Total Paid Amount", money(mDCB?.collection)],
          ["Total Due", money(mDCB?.balance)],
          ["Paid %", `${Math.round(n0(mDCB?.collection_percentage))}%`],
        ],
      },

      {
        title: "Today's Collections",
        icon: <Calendar size={18} />,
        items: [
          ["Total Paid", money(mReceipts?.todays_paid)],
          ["Water Paid", money(mReceipts?.todays_water_paid)],
          ["SLRM Paid", money(mReceipts?.todays_slrm_paid)],
          ["No. of Receipts", num(mReceipts?.todays_receipt_count)],
        ],
      },

      {
        title: "This Month's Collections",
        icon: <Calendar size={18} />,
        items: [
          ["Total Paid", money(mReceipts?.month_paid)],
          ["Water Paid", money(mReceipts?.month_water_paid)],
          ["SLRM Paid", money(mReceipts?.month_slrm_paid)],
          ["No. of Receipts", num(mReceipts?.month_receipt_count)],
        ],
      },

      {
        title: "Today's Bills",
        icon: <Droplet size={18} />,
        items: [
          ["Water Bills Count", num(mBills?.todays_water_bill_count)],
          ["Water Bills Amount", money(mBills?.todays_water_bill_amount)],
          ["SLRM Bills Count", num(mBills?.todays_slrm_bill_count)],
          ["SLRM Bills Amount", money(mBills?.todays_slrm_bill_amount)],
        ],
      },

      {
        title: "This Month's Bills",
        icon: <Droplet size={18} />,
        items: [
          ["Water Bills Count", num(mBills?.month_water_bill_count)],
          ["Water Bills Amount", money(mBills?.month_water_bill_amount)],
          ["SLRM Bills Count", num(mBills?.month_slrm_bill_count)],
          ["SLRM Bills Amount", money(mBills?.month_slrm_bill_amount)],
        ],
      },
    ];
  }, [h, s, mReceipts, mBills, mDCB]);

  return (
    <Row className="g-3">
      {cards.map((c, idx) => (
        <Col key={idx} xs={12} md={6} xl={4} className="d-flex">
          <SectionCard title={c.title} icon={c.icon} items={c.items} />
        </Col>
      ))}
    </Row>
  );
}